/**
 * CitationReferences — the "Sources" list under an answer.
 *
 * Every citation on the response appears here, whether or not a marker in the
 * prose happens to point at it: the list IS the answer's evidence, and a
 * source the model relied on going unlisted is the failure this guards.
 *
 * Each entry carries what the citation knows — authority, jurisdiction, date,
 * type, pinpoint — and, below that, two things that must never be confused:
 *
 *   `quote`    a VERBATIM span of the retrieved document. Quotation treatment:
 *              left rule, serif, quotation marks. This is the only text here
 *              the source actually contains.
 *   `asserts`  the sentence in the ANSWER that carried this marker. Model
 *              text. Labelled "Cited for:", muted, never in a quote treatment.
 *
 * BUG-01 was the collision between them. `supports` — `asserts` under its old
 * name — is a sentence lifted from the GENERATED answer, and this component
 * rendered it directly under the source title in the typography of a pinpoint
 * quotation, while `citation.snippet`, the real excerpt, was rendered nowhere.
 * A 2009 pleading therefore appeared annotated with a sentence about a 2012
 * reform: the document was real, the date was real, the annotation was
 * generated, and nothing in this list said so.
 *
 * A source with no `quote` says so, rather than borrowing the answer's words.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PiCaretDownLight, PiCaretRightLight } from 'react-icons/pi';
import { ResearchCitation } from '../types';
import {
  citationHeadline,
  citationMeta,
  jumpSections,
  sectionLabel,
} from '../citationMarkers';
import { CitationHandlers, openCitation } from './CitationChip';

interface Props extends CitationHandlers {
  citations: ResearchCitation[];
  /** Collapsed by default under a short answer; open under a document. */
  defaultOpen?: boolean;
}

const CitationReferences: React.FC<Props> = ({
  citations,
  defaultOpen = true,
  ...handlers
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);

  if (!citations || citations.length === 0) return null;

  return (
    <section
      className="mt-4 pt-3 border-t border-hairline-soft"
      data-testid="citation-references"
      /*
        ONE block for the reveal, and the LAST one.

        The evidence arrives after the answer it supports — so this section is
        taken whole rather than by its rows: a dozen staggered fragments under
        a finished answer reads as a list assembling itself, where the whole
        list fading in once reads as the answer being footnoted. It also fixes
        what the reader actually saw before, which was the sources standing
        there at full opacity while the prose above them was still arriving.
      */
      data-reveal-block
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="references-toggle"
        aria-expanded={open}
        className="flex items-center gap-1.5 text-[11px] font-semibold uppercase
                   tracking-wide text-fg-muted hover:text-fg-strong
                   transition-colors duration-200 ease-slide"
      >
        {open
          ? <PiCaretDownLight className="w-3 h-3" aria-hidden />
          : <PiCaretRightLight className="w-3 h-3" aria-hidden />}
        {t('Sources')} ({citations.length})
      </button>

      {open && (
        <ol className="mt-2 space-y-2" data-testid="references-list">
          {citations.map((citation) => {
            const meta = citationMeta(citation);
            return (
              <li
                key={citation.id || citation.number}
                data-testid="reference"
                data-number={citation.number}
                className="flex gap-2 text-xs"
              >
                <span className="text-fg-muted tabular-nums flex-shrink-0 pt-0.5">
                  {citation.marker || `[${citation.number}]`}
                </span>

                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => openCitation(citation, handlers)}
                    data-testid="reference-open"
                    className={clsx(
                      'text-left font-medium text-fg-strong hover:text-champagne',
                      'transition-colors duration-200 ease-slide'
                    )}
                  >
                    {citationHeadline(citation)}
                  </button>

                  {meta.length > 0 && (
                    <p className="text-[11px] text-fg-muted" data-testid="reference-meta">
                      {meta.join(' · ')}
                    </p>
                  )}

                  {/* WHAT THE DOCUMENT SAYS. Verbatim, and the only text in
                      this entry that gets a quotation treatment. */}
                  {citation.quote ? (
                    <button
                      type="button"
                      onClick={() => openCitation(citation, handlers)}
                      data-testid="reference-quote"
                      data-exact={citation.quote_exact ? 'true' : 'false'}
                      className="mt-1 block w-full text-left border-l-2 border-hairline-soft
                                 pl-2 font-serif text-[11px] leading-relaxed text-fg-body
                                 hover:border-champagne transition-colors duration-200 ease-slide"
                      title={t('Open passage')}
                    >
                      &ldquo;{citation.quote}&rdquo;
                      {citation.pinpoint && (
                        <cite className="not-italic ml-1 text-[10px] text-fg-muted">
                          — {citation.pinpoint}
                        </cite>
                      )}
                    </button>
                  ) : (
                    <p className="mt-1 text-[10px] italic text-fg-muted"
                       data-testid="reference-no-quote">
                      {t('No passage located')}
                    </p>
                  )}

                  {/* WHAT THE ANSWER CLAIMS. Model-written, and said to be. */}
                  {(citation.asserts || citation.supports) && (
                    <p className="mt-0.5 text-[11px] text-fg-muted"
                       data-testid="reference-asserts">
                      <span className="font-medium">{t('Cited for')}:</span>{' '}
                      {citation.asserts || citation.supports}
                    </p>
                  )}
                  {citation.justification && (
                    <p className="text-[11px] text-fg-muted italic"
                       data-testid="reference-justification">
                      {citation.justification}
                    </p>
                  )}

                  {/* More than one passage in this document matched, so the
                      reader gets to choose which one to land on. A single
                      match needs no list — the reference already goes there. */}
                  {jumpSections(citation).length > 0 && (
                    <div className="mt-1" data-testid="reference-sections">
                      <p className="text-[10px] text-fg-muted">
                        {jumpSections(citation).length} {t('matching passages')}
                      </p>
                      <ul className="mt-0.5 space-y-0.5">
                        {jumpSections(citation).map((section, i) => (
                          <li key={`${section.heading}-${section.start_char}-${i}`}>
                            <button
                              type="button"
                              onClick={() => openCitation(citation, handlers, section)}
                              data-testid="section-jump"
                              data-index={i}
                              className="text-left text-[11px] text-fg-body hover:text-champagne
                                         transition-colors duration-200 ease-slide"
                            >
                              <span className="text-fg-muted mr-1">↳</span>
                              {sectionLabel(section, i)}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
};

export default CitationReferences;
